import { forwardRef, HTMLAttributes, Ref, useCallback, useImperativeHandle, useRef, useState } from 'react';

import { IComponent } from '../../models/components.models';
import { useClassNames, useDimensions } from '../../utils/hooks';
import { ListRow, ListRowType } from '../ListRow/ListRow';
import { listRowStylesThinWithBorder } from '../ListRow/ListRow.styles';
import { basicTableStylesDefault, IBasicTableStyles } from './BasicTable.styles';

// It is minimize props of IColumn of fluent.
// try not to use the fluent resources since it is for Chat component. Chat will not have any dependencies from fluent since it could be used anywhere.
// If need more features, we can add more props
export interface IBasicColumn {
  key: string;
  name: string;
  fieldName?: string;
  flexGrow?: number;
  minWidth: number;
  maxWidth?: number;
  rowWidth?: number;
  calculatedWidth?: number;
  currentWidth?: number;
  onRender?: (rowIndex: number, cellIndex: number, column: IBasicColumn, item: any) => any;
  onRenderHeader?: (column: IBasicColumn) => any;
  data?: { [key: string]: any };
}

const calcColumnsWidth = (rowWidth: number, columns: IBasicColumn[]): IBasicColumn[] => {
  /**
   * width rules
   * minWidth is required,
   * the remain width will be applied to columns with the minWidth portion unless there are flexGrow columns. It will not be bigger than the maxWidth.
   * If there are flexGrow columns, the remain width will be applied to the flexGrow columns.
   */
  const widthInfo: {
    totalMinWidth: number;
    hasFlex: boolean;
    flexColumns: { i: number; flex: number }[];
    totalFlex: number;
  } = {
    totalMinWidth: 0,
    hasFlex: false,
    flexColumns: [],
    totalFlex: 0,
  };
  columns.forEach((column, i) => {
    column.rowWidth = rowWidth;
    const { minWidth, flexGrow } = column;
    widthInfo.totalMinWidth += minWidth;
    column.calculatedWidth = minWidth;
    if (flexGrow) {
      widthInfo.hasFlex = true;
      widthInfo.flexColumns.push({ i, flex: flexGrow });
      widthInfo.totalFlex += flexGrow;
    }
  });
  let remainWidth = rowWidth - widthInfo.totalMinWidth;
  if (remainWidth > 0) {
    if (widthInfo.hasFlex) {
      const flexUnitWidth = remainWidth / widthInfo.totalFlex;
      widthInfo.flexColumns.forEach(({ flex, i }) => {
        columns[i].calculatedWidth = (columns[i].calculatedWidth || 0) + flex * flexUnitWidth;
      });
    } else {
      columns.forEach((column) => {
        // portion
        const maxWidth = column.maxWidth || 0;
        let portionWidth = (column.minWidth / widthInfo.totalMinWidth) * remainWidth;
        let width = portionWidth + column.minWidth;
        if (maxWidth && width >= maxWidth) {
          portionWidth = maxWidth - column.minWidth;
          width = maxWidth;
          // it used maxWidth instead of minWidth
          widthInfo.totalMinWidth -= column.minWidth;
        }
        // later assigned width can be bigger than the previous columns since the maxWidth, but ignore it for now.
        remainWidth = remainWidth - portionWidth;
        column.calculatedWidth = width;
      });
      if (remainWidth) {
        const width = columns[columns.length - 1].calculatedWidth || 0;
        columns[columns.length - 1].calculatedWidth = width + remainWidth;
      }
    }
  }
  return columns;
};

const getColumns = (items: { [key: string]: any }[] = [], columns?: IBasicColumn[]) => {
  if (columns) {
    return columns;
  } else {
    if (items.length) {
      return Object.keys(items[0]).map(
        (key): IBasicColumn => ({
          key,
          fieldName: key,
          name: key,
          minWidth: 100,
        })
      );
    }
  }
  return [];
};

// used IColumn and IDetailsRow interface from Fluent for using ListRow and ListCell components.
export interface IBasicRowProps {
  itemIndex: number;
  columns: IBasicColumn[];
  item: { [key: string]: any };
  items: { [key: string]: any }[];
}

export interface IBasicTableProps extends IComponent<IBasicTableStyles>, HTMLAttributes<HTMLDivElement> {
  columns?: IBasicColumn[];
  items?: { [key: string]: any }[];
  onRenderRow?: (props: IBasicRowProps) => JSX.Element;
}

interface IBasicTableClassNames {
  root: string;
  columnHeader: string;
  cell: string;
  cellContents: string;
  rows: string;
  row: string;
}

export const BasicTable = forwardRef((props: IBasicTableProps, ref: Ref<HTMLDivElement>) => {
  const { className, styles, columns, items = [], onRenderRow, ...elementProps } = props;
  useImperativeHandle(ref, () => refRoot.current as HTMLDivElement);
  const [cols, setCols] = useState<IBasicColumn[]>();
  const refRoot = useRef<HTMLDivElement>(null);
  const updateColumnsWidth = useCallback(
    ({ width }: { width: number; height: number }) => {
      if (width) {
        const cols = calcColumnsWidth(width, getColumns(items, columns));
        setCols(cols);
      }
    },
    [columns, items]
  );
  useDimensions(refRoot, true, updateColumnsWidth);
  const classNames = useClassNames<Partial<IBasicTableStyles>, IBasicTableClassNames>(
    'BasicTable',
    basicTableStylesDefault,
    styles,
    className
  );
  return (
    <div {...elementProps} ref={refRoot} className={classNames.root}>
      <div className={classNames.columnHeader}>
        {cols &&
          cols.map((column) => {
            const { key, name, onRenderHeader, calculatedWidth } = column;
            return (
              <div key={key} className={classNames.cell} style={{ width: `${calculatedWidth}px` }}>
                <div className={classNames.cellContents}>{onRenderHeader ? onRenderHeader(column) : name}</div>
              </div>
            );
          })}
      </div>
      <div className={classNames.rows}>
        {cols &&
          items.map((item, rowIndex) => (
            <div key={rowIndex} className={classNames.row}>
              {onRenderRow ? (
                onRenderRow({ itemIndex: rowIndex, columns: cols, item, items })
              ) : (
                <ListRow
                  styles={listRowStylesThinWithBorder}
                  hideHeader
                  data={item}
                  columns={cols}
                  type={ListRowType.Basic}
                />
              )}
            </div>
          ))}
      </div>
    </div>
  );
});
